import { useState } from "react";
import {
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";

export default function ContactUs() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    category: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const categories = [
    "General Inquiry",
    "Technical Support",
    "Food Safety Concern",
    "Account Issues",
    "Partnership Opportunities",
    "Media Inquiries",
    "Feedback & Suggestions",
    "Report a Problem",
  ];

  const contactMethods = [
    {
      icon: <EnvelopeIcon className="w-6 h-6" />,
      title: "Email Support",
      description: "Get help via email",
      contact: "support@mealbridge.lk",
      availability: "24/7 - Response within 24 hours",
    },
    {
      icon: <PhoneIcon className="w-6 h-6" />,
      title: "Phone Support",
      description: "Speak with our team",
      contact: "+94 11 234 5678",
      availability: "Mon-Fri, 9:00 AM - 6:00 PM",
    },
    {
      icon: <ChatBubbleLeftRightIcon className="w-6 h-6" />,
      title: "Live Chat",
      description: "Instant messaging support",
      contact: "Available on website",
      availability: "Mon-Fri, 9:00 AM - 6:00 PM",
    },
  ];

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Simulate form submission
    setTimeout(() => {
      setLoading(false);
      setSuccess(true);
      setFormData({
        name: "",
        email: "",
        subject: "",
        category: "",
        message: "",
      });
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/20">
      {/* Header */}
      <section className="relative py-16 overflow-hidden text-white lg:py-20">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-teal-700 to-cyan-600"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-50"></div>

        <div className="relative z-10 text-center container-modern">
          <div className="inline-flex items-center px-6 py-3 mb-8 border-2 rounded-full shadow-2xl bg-white/20 backdrop-blur-xl border-white/30">
            <span className="text-sm font-bold">📞 Get in Touch</span>
          </div>

          <EnvelopeIcon className="w-16 h-16 mx-auto mb-6 text-white" />
          <h1 className="mb-6 text-3xl font-bold md:text-4xl lg:text-5xl">
            Contact Us
          </h1>
          <p className="max-w-3xl mx-auto text-lg text-white/95 font-medium md:text-xl">
            We're here to help! Reach out to us with any questions, concerns, or
            feedback.
          </p>
        </div>
      </section>

      <div className="px-4 py-12 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Contact Methods */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="mb-6 text-2xl font-bold text-slate-900 lg:text-3xl">
              Get in Touch
            </h2>
            <p className="max-w-2xl mx-auto text-base text-slate-600 font-medium">
              Choose the most convenient way to reach us
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {contactMethods.map((method, index) => (
              <div key={index} className="text-center card-static">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-lg bg-emerald-100 text-emerald-600">
                  {method.icon}
                </div>
                <h3 className="mb-2 text-lg font-semibold text-slate-900">
                  {method.title}
                </h3>
                <p className="mb-3 text-sm text-slate-600">
                  {method.description}
                </p>
                <p className="mb-2 text-sm font-medium text-emerald-600">
                  {method.contact}
                </p>
                <div className="flex items-center justify-center space-x-1 text-xs text-slate-500">
                  <ClockIcon className="w-4 h-4" />
                  <span>{method.availability}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
          {/* Contact Form */}
          <div className="card-static">
            <div className="mb-6">
              <h2 className="mb-2 text-xl font-bold text-slate-900">
                Send us a Message
              </h2>
              <p className="text-sm text-slate-600">
                Fill out the form below and we'll get back to you within 24
                hours
              </p>
            </div>

            {success && (
              <div className="p-4 mb-6 border border-green-200 rounded-lg bg-green-50">
                <p className="text-sm text-green-800">
                  Thank you for your message! We'll get back to you within 24
                  hours.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label
                    htmlFor="contact-name"
                    className="block mb-2 text-sm font-medium text-slate-700"
                  >
                    Full Name *
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    className="input-field"
                    placeholder="Enter your full name"
                    aria-label="Full name"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="contact-email"
                    className="block mb-2 text-sm font-medium text-slate-700"
                  >
                    Email Address *
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    className="input-field"
                    placeholder="Enter your email address"
                    aria-label="Email address"
                    required
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="contact-category"
                  className="block mb-2 text-sm font-medium text-slate-700"
                >
                  Category *
                </label>
                <select
                  id="contact-category"
                  value={formData.category}
                  onChange={(e) =>
                    handleInputChange("category", e.target.value)
                  }
                  className="input-field"
                  aria-label="Contact category"
                  required
                >
                  <option value="">Select a category</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">
                  Subject *
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => handleInputChange("subject", e.target.value)}
                  className="input-field"
                  placeholder="Brief description of your inquiry"
                  required
                />
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">
                  Message *
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => handleInputChange("message", e.target.value)}
                  rows={5}
                  className="input-field"
                  placeholder="Please provide details about your inquiry..."
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full btn-static-primary"
                disabled={loading}
              >
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>

          {/* Additional Information */}
          <div className="space-y-6">
            {/* Office Information */}
            <div className="card-static">
              <h3 className="mb-4 text-lg font-semibold text-slate-900">
                Our Office
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <MapPinIcon className="w-5 h-5 mt-1 text-slate-400" />
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      Address
                    </p>
                    <p className="text-sm text-slate-600">
                      123 Galle Road
                      <br />
                      Colombo 03, Sri Lanka
                      <br />
                      00300
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <ClockIcon className="w-5 h-5 mt-1 text-slate-400" />
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      Office Hours
                    </p>
                    <p className="text-sm text-slate-600">
                      Monday - Friday: 9:00 AM - 6:00 PM
                      <br />
                      Saturday: 9:00 AM - 2:00 PM
                      <br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Link */}
            <div className="card-static">
              <div className="flex items-center mb-3 space-x-3">
                <QuestionMarkCircleIcon className="w-5 h-5 text-emerald-600" />
                <h3 className="text-lg font-semibold text-slate-900">
                  Frequently Asked Questions
                </h3>
              </div>
              <p className="mb-4 text-sm text-slate-600">
                Before contacting us, you might find your answer in our
                comprehensive FAQ section.
              </p>
              <button className="btn-static-outline">View FAQ</button>
            </div>

            {/* Emergency Contact */}
            <div className="p-4 border border-red-200 rounded-lg bg-red-50">
              <h3 className="mb-3 text-base font-semibold text-red-800">
                Emergency Food Safety Issues
              </h3>
              <p className="mb-4 text-xs text-red-700">
                If you have an urgent food safety concern that poses immediate
                health risks, please contact us immediately:
              </p>
              <div className="space-y-1">
                <p className="text-sm font-medium text-red-800">
                  Emergency Hotline: +94 11 234 5679
                </p>
                <p className="text-sm font-medium text-red-800">
                  Email: emergency@mealbridge.lk
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
